<template>
  <div class="container-fluid">
    <div class="row fme-new-edit__title fme-montserrat-font">
      <div class="col-md-9">
        <h3>
          {{
            $t(`supervision.crud.page.${crudCurrentPageTitle}`) | textUpperCase
          }}
        </h3>
      </div>

      <div class="col-md-3 fme-new-edit__buttons text-right">
        <BaseButtonEvent
          :btText="$t('supervision.crud.button.goBack')"
          btClickType="goBack"
          btId="newEditeGoBack"
          btClickValue=""
          btUrlGoBack="CrudUserListPage"
          btCss="btn fme-btn fme-btn-secondary"
        />

        <span v-if="isUserAllowToEdit">
          <BaseButtonEvent
            v-if="userDeactivatedCrud === null"
            :btText="$t('supervision.crud.button.disable')"
            btClickType="sendDeleteCrud"
            :btClickValue="id"
            btId="userIdCrud"
            :btUrlGo="urlDeactivate"
            btUrlGoBack="CrudUserListPage"
            btCss="btn fme-btn fme-btn-primary"
          />
          <BaseButtonEvent
            v-else
            :btText="$t('supervision.crud.button.enable')"
            btClickType="sendActivateCrud"
            :btClickValue="id"
            btId="userIdCrud"
            :btUrlGo="urlActivate"
            btCss="btn fme-btn fme-btn-primary fme-btn-back"
            btUrlGoBack="CrudUserListPage"
          />
        </span>
      </div>
    </div>

    <div class="row">
      <div class="col-md-8">
        <BaseInput
          :inputCurrentValue="userName"
          :inputTitle="$t('supervision.crud.form.user.name.title')"
          :inputPlaceholder="$t('supervision.crud.form.user.name.placeholder')"
          inputType="text"
          inputCss=""
          inputId="input-user-name"
          @returned-input-value="userInput('name', $event)"
          :inputErrorText="$t(userError.name)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>

      <div class="col-md-4">
        <BaseSelect
          selectId="select-role"
          :selectValues="selectItems"
          selectCss=""
          :selectCurrentValue="userRole"
          @returnedSelectValue="userSelect('role', $event)"
          :selectErrorText="$t(userError.role)"
          :selectDisabled="!isUserAllowToEdit"
        />
      </div>
    </div>

    <div class="row fme-new-edit__row">
      <div class="col-md-4">
        <BaseInput
          :inputCurrentValue="userEmail"
          :inputTitle="$t('supervision.crud.form.user.email.title')"
          :inputPlaceholder="$t('supervision.crud.form.user.email.placeholder')"
          inputType="email"
          inputCss=""
          inputId="input-user-email"
          @returned-input-value="userInput('email', $event)"
          :inputErrorText="$t(userError.email)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>

      <div class="col-md-4">
        <BaseInput
          :inputCurrentValue="userPassword"
          :inputTitle="$t('supervision.crud.form.user.password.title')"
          :inputPlaceholder="
            $t('supervision.crud.form.user.password.placeholder')
          "
          inputType="password"
          inputCss=""
          inputId="input-user-password"
          @returned-input-value="userInput('password', $event)"
          :inputErrorText="$t(userError.password)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>

      <div class="col-md-4">
        <BaseInput
          :inputCurrentValue="userPhone"
          :inputTitle="$t('supervision.crud.form.user.phone.title')"
          :inputPlaceholder="$t('supervision.crud.form.user.phone.placeholder')"
          inputType="phone"
          inputCss=""
          inputId="input-user-title"
          @returned-input-value="userInput('phone', $event)"
          :inputErrorText="$t(userError.phone)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>
    </div>

    <div class="row fme-new-edit__row">
      <div class="col-md-6">
        <BaseInput
          :inputCurrentValue="userIdentifier"
          :inputTitle="$t('supervision.crud.form.user.identifier.title')"
          :inputPlaceholder="
            $t('supervision.crud.form.user.identifier.placeholder')
          "
          inputType="text"
          inputCss=""
          inputId="input-user-identifier"
          @returned-input-value="userInput('identifier', $event)"
          :inputErrorText="$t(userError.identifier)"
          :inputDisabled="!isUserAllowToEdit"
        />
      </div>

      <div class="col-md-6">
        <BaseSelect
          selectId="select-locale"
          :selectValues="selectLocale"
          selectCss=""
          :selectCurrentValue="userLocale"
          @returnedSelectValue="userSelect('locale', $event)"
          :selectErrorText="$t(userError.locale)"
          :selectDisabled="!isUserAllowToEdit"
        />
      </div>
    </div>

    <div class="row">
      <div class="col-md-6" style="margin: 10px 2px">
        <input
          type="checkbox"
          id="user-notification"
          v-model="userNotification"
          :disabled="!isUserAllowToEdit"
        />
        <label for="user-notification" style="white-space: pre">
          {{
            $t("supervision.crud.form.location.userNotification.placeholder")
          }}</label
        >
      </div>
    </div>

    <div v-if="isUserAllowToEdit" class="row fme-new-edit__row--last text-right">
      <div class="col-md-12">
        <BaseButtonEvent
          :btText="$t('supervision.crud.button.save')"
          :btClickType="saveValues.type"
          :btClickValue="newUser"
          :btId="saveValues.id"
          :btUrlGo="saveValues.urlGo"
          :btUrlGoBack="saveValues.urlBack"
          btCss="btn fme-btn fme-btn-primary"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { fetchFeatureToggles } from '../../../../services/featureToggler';
import { checkIfUserIsAllowToEdit } from '../../../../utils/checkIfUserIsAllowToEdit';
import { getV3Token } from '../../../../utils/getV3Token'; 
import { getUserRole } from '../../../../utils/getUserRole'; 

import axios from "axios";

const BaseInput = () => import("@/components/fragment/input/BaseInput");
const BaseSelect = () => import("@/components/fragment/select/BaseSelect");
const BaseButtonEvent = () =>
  import("@/components/fragment/button/BaseButtonEvent");

export default {
  name: "EditPage",
  async created() {
    this.clearStore();
    this.hasUser();
    this.changeCrudCurrentPageTitle("user");
    await this.getFeatures();
    this.checkUserPermissions();
  },
  update() {
    this.hasUser();
  },
  beforeDestroy() {
    this.clearStore();
    this.changeErrors([]);
  },
  data() {
    return {
      selectItems: {
        default: "supervision.crud.select.users.title",
        options: [
          { name: "supervisor", value: "users.supervisor" },
          { name: "coordinator", value: "users.coordinator" },
          { name: "station", value: "users.station" },
          { name: "client", value: "users.client" },
          { name: "monitoring", value: "users.monitoring" },
        ],
      },
      selectLocale: {
        default: "supervision.crud.select.locale.title",
        options: [
          { name: "pt-BR", value: "locale.ptBR" },
          { name: "es", value: "locale.es" },
        ],
      },
      saveValues: {
        type: "sendPutCrudUser",
        id: "btUpdateUser",
        urlGo: `${process.env.VUE_APP_CRUD_URL_USERS}/${this.uuid}`,
        urlBack: "CrudUserListPage",
      },
      userError: {
        name: "",
        role: "",
        email: "",
        password: "",
        phone: "",
        identifier: "",
        locale: "",
      },
      userNotification: "",
      isUserAllowToEdit: false
    };
  },
  props: {
    id: {
      type: String,
    },
    uuid: {
      type: String,
    },
  },
  components: {
    BaseInput,
    BaseSelect,
    BaseButtonEvent,
  },
  computed: {
    ...mapGetters(["axiosHeaders1", "headersCrud"]),
    ...mapGetters("ModuleErrors", ["errorsList"]),
    ...mapGetters("ModuleSupervision", [
      "userCrud",
      "userIdCrud",
      "userNameCrud",
      "userRoleCrud",
      "userEmailCrud",
      "userPasswordCrud",
      "userPhoneCrud",
      "userIdentifierCrud",
      "userLocaleCrud",
      "userDeactivatedCrud",
      "crudCurrentPageTitle",
    ]),
    userId() {
      return this.userIdCrud;
    },
    userName() {
      return this.userNameCrud;
    },
    userRole() {
      return this.userRoleCrud;
    },
    userEmail() {
      return this.userEmailCrud;
    },
    userPassword() {
      return this.userPasswordCrud;
    },
    userPhone() {
      return this.userPhoneCrud;
    },
    userIdentifier() {
      return this.userIdentifierCrud;
    },
    userLocale() {
      return this.userLocaleCrud;
    },
    newUser() {
      let user = {
        name: this.userNameCrud,
        role: this.userRoleCrud,
        email: this.userEmailCrud,
        password: this.userPasswordCrud,
        phone: this.userPhoneCrud,
        identifier: this.userIdentifierCrud,
        locale: this.userLocale,
        userNotification: this.userNotification ? true : "",
      };
      if (!this.userPasswordCrud) delete user.password;
      return user;
    },
    urlActivate() {
      return `${process.env.VUE_APP_CRUD_URL_USERS}/${this.uuid}/activate`;
    },
    urlDeactivate() {
      return `${process.env.VUE_APP_CRUD_URL_USERS}/${this.uuid}/deactivate`;
    },
  },
  methods: {
    ...mapActions("ModuleErrors", ["changeErrors"]),
    ...mapActions("ModuleSupervision", [
      "changeUserIdCrud",
      "changeUserNameCrud",
      "changeUserRoleCrud",
      "changeUserEmailCrud",
      "changeUserPasswordCrud",
      "changeUserPhoneCrud",
      "changeUserIdentifierCrud",
      "changeUserActiveCrud",
      "changeUserDeactivatedCrud",
      "changeUserLocaleCrud",
      "changeCrudCurrentPageTitle",
    ]),
    hasUser() {
      if (this.id !== "" && !isNaN(this.id)) {
        let url = `${process.env.VUE_APP_CRUD_URL_USERS}/${this.uuid}`;
        axios
          .get(url, {
            headers: this.headersCrud,
          })
          .then((response) => {
            return response.data;
          })
          .then((user) => {
            let url_notification = `${process.env.VUE_APP_USERS_NOTIFICATION}/${this.uuid}`;
            let notification = "";
            axios
              .get(url_notification, {
                headers: this.axiosHeaders1,
              })
              .then((data_notification) => {
                notification = data_notification.data.data.notification;
                this.setUser(user, notification);
              });
          })
          .catch((e) => {
            if (e.response.status && e.response.status === 401) {
              this.logoutUser();
            } else {
              console.log(e);
            }
          });
      } else {
        this.$router.push({ name: "CrudUserListPage" });
      }
    },
    setUser(user, notification) {
      this.changeUserIdCrud(user.id);
      this.changeUserNameCrud(user.name);
      this.changeUserEmailCrud(user.email);
      this.changeUserPasswordCrud(user.password);
      this.changeUserPhoneCrud(user.phone);
      this.changeUserIdentifierCrud(user.identifier);
      this.changeUserRoleCrud(user.role);
      this.changeUserLocaleCrud(user.locale);
      this.changeUserDeactivatedCrud(user.deactivatedAt);
      if (notification) {
        this.userNotification = true;
      } else {
        this.userNotification = "";
      }
    },
    userInput(refValue, eventValue) {
      if (refValue === "name") {
        this.changeUserNameCrud(eventValue);
      } else if (refValue === "email") {
        this.changeUserEmailCrud(eventValue);
      } else if (refValue === "password") {
        this.changeUserPasswordCrud(eventValue);
      } else if (refValue === "phone") {
        this.changeUserPhoneCrud(eventValue);
      } else if (refValue === "identifier") {
        this.changeUserIdentifierCrud(eventValue);
      }
    },
    userSelect(refValue, event) {
      if (refValue === "role") {
        this.changeUserRoleCrud(event);
      } else if (refValue === "locale") {
        this.changeUserLocaleCrud(event);
      }
    },
    clearStore() {
      this.changeUserIdCrud("");
      this.changeUserNameCrud("");
      this.changeUserEmailCrud("");
      this.changeUserPasswordCrud("");
      this.changeUserPhoneCrud("");
      this.changeUserIdentifierCrud("");
      this.changeUserRoleCrud("");
      this.changeUserLocaleCrud("");
      this.changeUserDeactivatedCrud("");
    },
    async getFeatures() {
      try {
        const response = await fetchFeatureToggles(getV3Token());
        this.features = response;
      } catch (error) {
        console.error(error);
      }
    },
    checkUserPermissions() {
      if (this.features) {
        this.isUserAllowToEdit = checkIfUserIsAllowToEdit(this.features, getUserRole());
      } else {
        console.error("Features are not available yet.");
      }
    }
  },
  filters: {},
  watch: {
    errorsList() {
      this.userError.name = "";
      this.userError.role = "";
      this.userError.password = "";
      this.userError.email = "";
      this.userError.identifier = "";
      this.userError.phone = "";
      this.userError.locale = "";

      this.errorsList.map((error) => {
        if(error.error === "User does not have permission to perform this action") {
          this.userError.role = `errors.role.unauthorized`;
        }

        if (error.property.indexOf("name") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.name = `errors.name.required`;
          } else if (
            this.hasProperty(error.constraints, "IsUsernameUniqueConstraint")
          ) {
            this.userError.name = `errors.name.uniqueness`;
          }
        }

        if (error.property.indexOf("role") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.role = `errors.role.enum`;
          }
        }

        if (error.property.indexOf("password") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.password = `errors.password.required`;
          }
        }

        if (error.property.indexOf("email") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.email = `errors.email.required`;
          } else if (
            this.hasProperty(error.constraints, "IsUserEmailUniqueConstraint")
          ) {
            this.userError.email = `errors.email.uniqueness`;
          } else if (
            this.hasProperty(error.constraints, "isEmail")
          ) {
            this.userError.email = `errors.email.format`;
          }
        }

        if (error.property.indexOf("identifier") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.identifier = `errors.identifier.required`;
          } else if (
            this.hasProperty(error.constraints, "IsUserIdentifierUniqueConstraint")
          ) {
            this.userError.identifier = `errors.identifier.uniqueness`;
          }
        }
        if (error.property.indexOf("locale") !== -1) {
          if (this.hasProperty(error.constraints, "isNotEmpty")) {
            this.userError.locale = `errors.locale.required`;
          }
        }
      });
    },
  },
};
</script>

<style lang="scss">
</style>
